import React, { useEffect, useState } from 'react';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';

import useLocale from '@/hooks/useLocale';
import { useHistory } from 'react-router';

import { useParams } from 'react-router-dom';
import * as Styles from './ProductTemplatesPage.styles';

import { locale } from '@/utils/locale';
import { vpsOciActions } from '@/redux/modules/productDetail/vpsOci';

import VpsStoreLoaderPage from '@/pages/Products/VpsOciDetailPage/pages/VpsStoreLoaderPage';
import VpsStoreModal from '@/pages/Products/VpsOciDetailPage/pages/ProductTemplatesPage/commons/VpsStoreModal';

import Rebuild from './components/Rebuild/Rebuild';
import ProductTemplateAbout from './components/ProductTemplateAbout/ProductTemplateAbout';
import CurrentSOInfo from './components/CurrentSOInfo/CurrentSOInfo';
import AvailableOptions from './components/AvailableOptions/AvailableOptions';
import useServerStatus from '@/hooks/useServerStatus';
import { VpsOciStatusEnum } from '@/pages/Products/products.enum';

const ProductTemplatesPage = ({
  setShowProductTemplatesPage,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id: hostingId } = useParams();
  const [modalType, setModalType] = useState('confirmRebuild');

  const {
    vpsData,
    invoiceGenerated,
    imageInvoiceId,
    rebuildStatus,
    changeImageStatus,
  } = useSelector(state => ({
    vpsData: state.vpsOci?.data,
    invoiceGenerated: state.vpsOci?.changeImage?.data?.invoiceGenerated,
    imageInvoiceId: state.vpsOci?.changeImage?.data?.invoiceId,
    rebuildStatus: state.vpsOci?.rebuild?.status,
    changeImageStatus: state.vpsOci?.changeImage?.status,
    statusData: state.vpsOci?.status?.value,
  }), shallowEqual);

  const statusQueue = rebuildStatus || changeImageStatus;
  const statusQueueIsSuccess = statusQueue === 'success';
  const serverInfo = useSelector(state => state.vpsOci?.data?.serverInfo);

  const {
    serverStatus,
  } = useServerStatus({
    serverInfo,
    serverId: hostingId,
  });
  const statusVpsOciIsOnline = serverStatus === VpsOciStatusEnum.ONLINE;

  const { productDetails: productDetailsLocale } = useLocale();
  const { vpsOci: vpsOciLocale } = productDetailsLocale;

  const handleCloseModal = () => {
    setModalType(null);
  };

  useEffect(() => {
    if (invoiceGenerated) {
      history.push(`${locale('routes.billing')}${locale('routes.unpaid')}/${imageInvoiceId}?openInvoice=true`);
      return;
    }

    if (statusQueue === 'server_inactive') {
      setModalType('serverInactive');
    } else if (statusQueue === 'limit_exceeded') {
      setModalType('limitExceeded');
    } else if (statusQueue === 'not_found') {
      setModalType('notFound');
    } else if (statusQueue === 'unknown_error') {
      setModalType('unknownError');
    } else if (statusQueue === 'unknown_error_change_image') {
      setModalType('unknownErrorChangeImage');
    }
  }, [statusQueue, history, imageInvoiceId, invoiceGenerated]);

  useEffect(() => {
    if (!statusQueueIsSuccess || statusVpsOciIsOnline || !hostingId) return undefined;

    const intervalId = setInterval(() => {
      dispatch(vpsOciActions.data.get({ hostingId }));
    }, 20000);

    return () => clearInterval(intervalId);
  }, [dispatch, hostingId, statusQueueIsSuccess, statusVpsOciIsOnline]);

  useEffect(() => {
    if (statusQueueIsSuccess && statusVpsOciIsOnline) {
      window.location.href = `${locale('routes.products')}${locale('routes.productdetail')}/${hostingId}`;
    }
  }, [statusQueueIsSuccess, statusVpsOciIsOnline, hostingId]);

  if (
    !vpsData
    || Object.keys(vpsData).length === 0
  ) return null;

  if (statusQueueIsSuccess) {
    return (
      <VpsStoreLoaderPage
        title={vpsOciLocale.store.rebuild.feedback.request.title}
        description={vpsOciLocale.store.rebuild.feedback.request.description}
        isLoaderBuilder
      />
    );
  }

  return (
    <Styles.Container data-testid="vpsoci-templates-page">
      <ProductTemplateAbout
        setShowProductTemplatesPage={setShowProductTemplatesPage}
      />

      <CurrentSOInfo />

      <AvailableOptions />

      <Rebuild />

      <VpsStoreModal
        modalType={modalType}
        onClose={handleCloseModal}
      />
    </Styles.Container>
  );
};

export default ProductTemplatesPage;
